import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import PublicNavbar from "../../components/layout/PublicNavbar";
import Footer from "../../components/layout/Footer";
import TrustBadge from "../../components/marketplace/TrustBadge";
import VerifiedBadge from "../../components/common/VerifiedBadge";
import { Card, Pill } from "../../components/common/Primitives";
import {
  ShieldCheck, BadgeCheck, Flame, MapPin, Building2, TrendingUp,
  AlertTriangle, Clock, ArrowRight, ExternalLink,
} from "lucide-react";
import { API_BASE } from "../../lib/api";
import { formatDate, timezoneSuffix } from "../../lib/datetime";

/** Bigger, prettier sparkline used only on the public passport hero. */
function BigSparkline({ points, width = 640, height = 96 }) {
  const totals = (points || []).map((p) => p.total).filter((n) => typeof n === "number");
  if (totals.length < 2) {
    return <div className="text-[11px] font-mono text-ink-faint py-6">Not enough history to display a trend yet.</div>;
  }
  const min = Math.min(...totals, 0);
  const max = Math.max(...totals, 100);
  const range = Math.max(1, max - min);
  const stepX = width / (totals.length - 1);
  const coords = totals.map((v, i) => [i * stepX, height - ((v - min) / range) * height]);
  const path = coords.map(([x, y], i) => (i === 0 ? `M ${x.toFixed(2)},${y.toFixed(2)}` : `L ${x.toFixed(2)},${y.toFixed(2)}`)).join(" ");
  const area = `${path} L ${width.toFixed(2)},${height.toFixed(2)} L 0,${height.toFixed(2)} Z`;
  const trending = totals[totals.length - 1] - totals[0];
  const stroke = trending >= 0 ? "#059669" : "#dc2626";
  const gradId = "vz-sparkline-grad";
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" data-testid="public-passport-sparkline">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} stroke="none" />
      <path d={path} fill="none" stroke={stroke} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Component pillar row — nicer visual than plain progress bars. */
const COMPONENT_ORDER = [
  "company", "filings", "financials", "documentation", "founder",
  "funding", "debt", "investor_fit", "red_flags",
];

const COMPONENT_LABELS = {
  company: "Company Verification",
  filings: "MCA / RoC Filings",
  financials: "Financial Health",
  documentation: "Documentation Quality",
  founder: "Founder Profile",
  funding: "Funding Profile",
  debt: "Debt & Liabilities",
  investor_fit: "Investor Fit",
  red_flags: "Red-Flag Screen",
};

function PillarRow({ name, score, coverage, weight }) {
  const pct = Math.max(0, Math.min(100, score));
  const covPct = Math.max(0, Math.min(100, coverage));
  const barColor = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-gold" : "bg-rose-400";
  return (
    <div className="grid grid-cols-12 items-center gap-3 py-2.5 border-b border-navy/5 last:border-b-0" data-testid={`pillar-${name}`}>
      <div className="col-span-4 sm:col-span-3 text-sm text-ink font-heading truncate">
        {COMPONENT_LABELS[name] || name.replace(/_/g, " ")}
      </div>
      <div className="col-span-6 sm:col-span-7">
        <div className="h-2 bg-navy/10 rounded overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <div className="h-1 bg-navy/5 rounded overflow-hidden mt-1">
          <div className="h-full bg-navy/25" style={{ width: `${covPct}%` }} />
        </div>
      </div>
      <div className="col-span-2 text-right">
        <div className="font-mono text-sm text-ink">{pct}<span className="text-[10px] text-ink-muted">/100</span></div>
        <div className="text-[10px] font-mono text-ink-muted">{covPct}% cov · w{weight}</div>
      </div>
    </div>
  );
}

function ExpiryPill({ expiresAt }) {
  if (!expiresAt) return null;
  try {
    const d = new Date(expiresAt);
    const now = new Date();
    const hrs = Math.round((d - now) / 36e5);
    if (hrs <= 0) return null;
    const label = hrs < 48 ? `Expires in ${hrs}h` : `Expires ${formatDate(expiresAt)}`;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-navy/15 text-[11px] font-mono text-ink-soft"
             data-testid="public-passport-expiry-pill">
        <Clock size={11} /> {label}
      </span>
    );
  } catch { return null; }
}

export default function PublicPassport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/public/passport/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setStatus(e?.response?.status || 0);
        setError(e?.response?.data?.detail || "Passport unavailable");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const expired = status === 410;
  const trustTotal = data?.trust_score?.total;
  const trustLabel = data?.trust_score?.label;

  return (
    <div className="min-h-screen text-ink bg-cream">
      <PublicNavbar />

      <main className="max-w-[1100px] mx-auto px-6 py-8">
        {loading ? (
          <div className="text-sm text-ink-muted" data-testid="public-passport-loading">Loading passport…</div>
        ) : error ? (
          <Card testId="public-passport-error">
            <div className="flex items-center gap-2 text-rose-700 mb-2">
              <AlertTriangle size={16} />
              <div className="overline">{expired ? "PASSPORT LINK EXPIRED" : "PASSPORT UNAVAILABLE"}</div>
            </div>
            <div className="text-sm text-ink-soft">{error}</div>
            <div className="mt-3 text-xs text-ink-muted">
              {expired
                ? "The founder set an expiry on this link. Ask them for a fresh one — links can be regenerated in seconds."
                : "The founder may have rotated or revoked this link. Ask them for the current one."}
            </div>
            <Link to="/" className="text-xs font-mono uppercase tracking-wider text-gold mt-4 inline-block">
              Back to VenturaZ →
            </Link>
          </Card>
        ) : (
          <div className="space-y-6">

            {/* ---- HERO ---- */}
            <div
              className="relative overflow-hidden rounded-xl border border-white/10 shadow-lg"
              style={{
                background:
                  "radial-gradient(900px 500px at 15% 15%, rgba(2,187,142,0.45) 0%, transparent 55%)," +
                  "radial-gradient(700px 500px at 92% 90%, rgba(23,149,189,0.55) 0%, transparent 60%)," +
                  "linear-gradient(120deg, #1F5B4A 0%, #2A9E7E 55%, #1A7F8C 100%)",
              }}
              data-testid="public-passport-hero"
            >
              {/* Concentric rings decoration */}
              <svg aria-hidden="true" className="absolute -left-16 -top-16 w-[340px] h-[340px] opacity-[0.13] pointer-events-none" viewBox="0 0 400 400">
                {[40, 80, 120, 160, 200].map((r) => (
                  <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#FFFFFF" strokeWidth="1" />
                ))}
              </svg>
              {/* Gold accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-gold via-gold/80 to-gold/20" />

              <div className="grid md:grid-cols-3 gap-6 p-6 md:p-8 items-center">
                {/* Left: brand + startup name */}
                <div className="md:col-span-2" style={{ color: "#f6f4ef" }}>
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-gold/90 flex items-center gap-1">
                    <ShieldCheck size={12} /> VENTURAZ VERIFIED PASSPORT
                  </div>
                  <h1
                    className="font-heading text-3xl sm:text-4xl md:text-5xl mt-2 leading-tight"
                    style={{ color: "#f6f4ef" }}
                    data-testid="public-passport-name"
                  >
                    {data.startup?.startup_name}
                  </h1>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" style={{ color: "rgba(246,244,239,0.85)" }}>
                    {data.startup?.verified && (
                      <Pill tone="gold"><BadgeCheck size={11} className="mr-1" /> Verified</Pill>
                    )}
                    {data.trust_badge?.tier && (
                      <VerifiedBadge badge={data.trust_badge} size="md" testId="public-passport-verified-badge" />
                    )}
                    {data.startup?.sector && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-mono">
                        <Building2 size={11} /> {data.startup.sector}
                      </span>
                    )}
                    {data.startup?.business_stage && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-mono">
                        <TrendingUp size={11} /> {data.startup.business_stage}
                      </span>
                    )}
                    {data.startup?.city && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-mono">
                        <MapPin size={11} /> {data.startup.city}
                      </span>
                    )}
                  </div>
                  {data.startup?.one_liner && (
                    <p className="mt-4 max-w-2xl leading-relaxed" style={{ color: "rgba(246,244,239,0.85)" }}>{data.startup.one_liner}</p>
                  )}
                </div>

                {/* Right: giant Trust Score card */}
                <div
                  className="bg-cream/95 rounded-lg border border-gold/40 p-5 text-center shadow-lg"
                  data-testid="public-passport-score-card"
                >
                  <div className="overline text-ink-muted">TRUST SCORE</div>
                  <div className="font-heading text-6xl text-ink leading-none mt-2" data-testid="public-passport-trust-total">
                    {trustTotal ?? "—"}
                  </div>
                  <div className="text-[11px] font-mono text-ink-muted mt-1">/ 100</div>
                  <div className="mt-3 flex justify-center">
                    <TrustBadge
                      total={trustTotal}
                      label={trustLabel}
                      size="md"
                      testIdPrefix="public-passport-trust"
                    />
                  </div>
                  {(data.streak?.streak_weeks || 0) >= 4 && (
                    <div className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-300 text-orange-700 text-[11px] font-mono"
                           data-testid="public-passport-streak">
                      <Flame size={11} /> {data.streak.streak_weeks}-week rising streak
                    </div>
                  )}
                </div>
              </div>

              {/* Meta strip */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-6 md:px-8 py-3 border-t border-white/10 text-[11px] font-mono uppercase tracking-widest"
                    style={{ backgroundColor: "rgba(15,61,46,0.4)", color: "rgba(246,244,239,0.8)" }}>
                <div className="flex items-center gap-2">
                  <span>Shared by founder</span>
                  <span style={{ color: "rgba(246,244,239,0.4)" }}>·</span>
                  <span>{data.shared_at ? formatDate(data.shared_at) : "—"}</span>
                  <span style={{ color: "rgba(246,244,239,0.4)" }}>·</span>
                  <span>{timezoneSuffix()}</span>
                </div>
                <ExpiryPill expiresAt={data.expires_at} />
              </div>
            </div>

            {/* ---- FOUNDER INTRO VIDEO (if uploaded) ---- */}
            <Card testId="public-passport-video">
              <div className="overline mb-3">FOUNDER · 60-SECOND INTRO</div>
              <div className="rounded-md overflow-hidden bg-black border border-navy/10" style={{ aspectRatio: "16 / 9" }}>
                <video
                  src={`${API_BASE}/public/passport/${token}/video`}
                  controls
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  data-testid="public-passport-video-player"
                  onError={(e) => {
                    // Gracefully hide the whole card if the founder hasn't uploaded a video.
                    const card = e.currentTarget.closest('[data-testid="public-passport-video"]');
                    if (card) card.style.display = "none";
                  }}
                />
              </div>
              <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-ink-muted">
                Uploaded by the founder · unedited by VenturaZ
              </div>
            </Card>

            {/* ---- SCORE TRAJECTORY ---- */}
            <Card testId="public-passport-history">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">SCORE TRAJECTORY · LAST 30 DAYS</div>
                {(data.history || []).length > 1 && (
                  <span className="text-[11px] font-mono text-ink-muted">
                    {data.history.length} snapshot{data.history.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <BigSparkline points={data.history} />
            </Card>

            {/* ---- VERIFICATION PILLARS ---- */}
            <Card testId="public-passport-components">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">VERIFICATION PILLARS</div>
                  <div className="text-xs text-ink-muted mt-1">
                    9 factors · component score + coverage. Raw data is only shown to approved investors on the private VenturaZ Passport.
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Strong
                  <span className="inline-block w-2 h-2 rounded-full bg-gold ml-2" /> Fair
                  <span className="inline-block w-2 h-2 rounded-full bg-rose-400 ml-2" /> Needs work
                </div>
              </div>
              <div>
                {COMPONENT_ORDER
                  .filter((k) => data.trust_score?.components?.[k])
                  .map((k) => {
                    const v = data.trust_score.components[k];
                    return (
                      <PillarRow
                        key={k}
                        name={k}
                        score={v.score}
                        coverage={v.coverage}
                        weight={v.weight_pct}
                      />
                    );
                  })}
              </div>
            </Card>

            {/* ---- CTA ---- */}
            <div className="rounded-xl border border-gold/40 bg-gradient-to-br from-white via-gold/[0.06] to-white p-6 sm:p-7 shadow-sm"
                  data-testid="public-passport-cta">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="overline">WANT THE FULL PICTURE?</div>
                  <div className="font-heading text-xl sm:text-2xl text-ink mt-1">
                    Financials, cap-table, KYC — behind investor approval.
                  </div>
                  <div className="text-sm text-ink-muted mt-1">
                    Join VenturaZ as an investor, get approved, and unlock the private Verified Passport.
                  </div>
                </div>
                <Link
                  to="/for-investors"
                  className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 rounded font-mono uppercase text-xs tracking-widest hover:bg-navy/90 transition-colors group"
                  data-testid="public-passport-become-investor"
                >
                  Become an Investor
                  <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>

            {/* ---- Watermark ---- */}
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted text-center pt-4 border-t border-navy/10"
                  data-testid="public-passport-watermark">
              {data.watermark}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-faint text-center">
              Operated by Ultiwise Ventures Private Limited · Noida · Powered by CorpZo
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
